import { RefObject, useEffect, useState } from "react";
import { experimentClient, isExperimentLoaded } from "analytics";

// The hide timer is a CSS animation that ends at opacity 1 (see the CSS
// module), so a wrapper that is still waiting but already opaque has been
// revealed by the timer, showing control.
const revealedByTimer = (wrapper: HTMLElement | null) =>
  !!wrapper && window.getComputedStyle(wrapper).opacity === "1";

/**
 * The variant Amplitude assigned this visitor, but only when the answer came
 * before the hide timer revealed control. A late answer keeps the visitor on
 * control for this page view and sends no exposure, so they are left out of
 * the experiment instead of watching control swap for their variant.
 *
 * Leaving them out keeps the arms comparable: whether the answer is late
 * depends on the connection, not on the arm, and it is settled before the
 * visitor has seen anything but control. It does shrink the experiment, and
 * the visitors it drops are the ones on the slowest connections.
 *
 * A returning visitor does not wait for the network: the SDK keeps every
 * answer in localStorage and loads it when the client is created, so a stored
 * variant is used at once. `start()` still refreshes the store for the next
 * page load; until then a stopped test or a changed rollout can show the old
 * variant for one page view.
 *
 * Exposure is tracked here by hand, because `all()` reads the variants
 * without the SDK's automatic exposure. `exposure()` skips fallback variants
 * just as the automatic one does, so a failed flag load still counts nobody.
 */
export const useVariantInTime = (
  flagKey: string,
  wrapper: RefObject<HTMLElement>
) => {
  const [state, setState] = useState<{
    answered: boolean;
    variant?: string;
  }>({ answered: false });

  useEffect(() => {
    let mounted = true;

    const answer = () => {
      if (!experimentClient || revealedByTimer(wrapper.current)) {
        setState({ answered: true });
        return;
      }

      experimentClient.exposure(flagKey);
      setState({
        answered: true,
        variant: experimentClient.all()[flagKey]?.value,
      });
    };

    if (experimentClient?.all()[flagKey]?.value) {
      answer();
      return;
    }

    isExperimentLoaded.then(() => {
      if (mounted) {
        answer();
      }
    });

    return () => {
      mounted = false;
    };
  }, [flagKey, wrapper]);

  return state;
};
